/**
 * Index mint configuration.
 *
 * Leave INDEX_MINT_LIVE empty pre-launch. Use `./scripts/go-live.sh <CA>`
 * to set it on the site + worker (local and Faith) in one shot.
 */

/** @type {string} Mainnet launch mint — empty until go-live */
const INDEX_MINT_LIVE = "gqZNgVc8VEaaU6ngAeEEDA7wbmnXTrE98bws4NVidex";

const INDEX_MINT = (INDEX_MINT_LIVE || "").trim();
const INDEX_IS_LIVE = Boolean(INDEX_MINT);
const INDEX_SYMBOL = "SI";
const INDEX_NAME = "Stonksindex";

const STONKFUN_TOKEN = INDEX_MINT
  ? `https://www.stonkfun.xyz/token/${INDEX_MINT}`
  : "https://www.stonkfun.xyz";
const SOLSCAN = INDEX_MINT
  ? `https://solscan.io/token/${INDEX_MINT}`
  : "https://solscan.io";
const JUPITER_SWAP = INDEX_MINT
  ? `https://jup.ag/swap/SOL-${INDEX_MINT}`
  : "https://jup.ag";

export {
  INDEX_MINT,
  INDEX_MINT_LIVE,
  INDEX_IS_LIVE,
  INDEX_SYMBOL,
  INDEX_NAME,
  STONKFUN_TOKEN,
  SOLSCAN,
  JUPITER_SWAP,
};

/** Build lottery public snapshot from live state/current files (worker-owned). */
export async function loadLotteryPublic() {
  try {
    const [state, current] = await Promise.all([
      fetch("/data/lottery-state.json", { cache: "no-store" }).then((r) =>
        r.ok ? r.json() : null,
      ),
      fetch("/data/lottery-current.json", { cache: "no-store" }).then((r) =>
        r.ok ? r.json() : null,
      ),
    ]);
    if (!state && !current) return null;
    const potLamports = Number(state?.potLamports) || 0;
    return {
      enabled: true,
      potSol: potLamports / 1e9,
      potLamports,
      nextDrawAt: state?.nextDrawAt || current?.nextDrawAt || null,
      lastDrawAt: state?.lastDrawAt || current?.createdAt || null,
      drawCount: state?.drawCount || current?.version || 0,
      lotteryPct: state?.lotteryPct ?? current?.lotteryPct ?? 10,
      intervalMin: state?.intervalMin ?? 20,
      intervalMax: state?.intervalMax ?? 60,
      minPotSol: state?.minPotSol ?? 0.01,
      equalOdds: true,
      lastWinner: current?.winner
        ? {
            address: current.winner,
            amountSol: current.amountSol,
            at: current.createdAt,
            version: current.version,
            signature: current.signature || null,
            eligibleCount: current.eligibleCount ?? null,
          }
        : null,
    };
  } catch {
    return null;
  }
}

export async function loadIndexData() {
  const res = await fetch("/stonk-index-data.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load index data (${res.status})`);
  const data = await res.json();
  // Worker-owned state files win when the bundled snapshot was clobbered by a site deploy
  const live = await loadLotteryPublic();
  if (live) {
    const snap = data.lottery || {};
    const snapCount = Number(snap.drawCount) || 0;
    const liveCount = Number(live.drawCount) || 0;
    if (
      !data.lottery ||
      liveCount > snapCount ||
      (live.lastWinner && !snap.lastWinner) ||
      (live.potSol != null && snap.potSol == null)
    ) {
      data.lottery = { ...snap, ...live };
    }
  }
  return data;
}

export async function loadBasketHistory() {
  try {
    const current = await fetch("/data/basket-current.json", { cache: "no-store" }).then((r) =>
      r.ok ? r.json() : null,
    );
    const versions = [];
    const max = current?.version || 10;
    await Promise.all(
      Array.from({ length: max }, (_, i) => i + 1).map(async (v) => {
        try {
          const r = await fetch(`/data/basket-history/v${v}.json`, { cache: "no-store" });
          if (r.ok) versions.push(await r.json());
        } catch {
          /* skip */
        }
      }),
    );
    versions.sort((a, b) => (b.version || 0) - (a.version || 0));
    return { current, versions };
  } catch {
    return { current: null, versions: [] };
  }
}

export async function loadCycleHistory() {
  try {
    const current = await fetch("/data/cycle-current.json", { cache: "no-store" }).then((r) =>
      r.ok ? r.json() : null,
    );
    const versions = [];
    const max = current?.version || 0;
    if (max > 0) {
      await Promise.all(
        Array.from({ length: max }, (_, i) => i + 1).map(async (v) => {
          try {
            const r = await fetch(`/data/cycle-history/v${v}.json`, { cache: "no-store" });
            if (r.ok) versions.push(await r.json());
          } catch {
            /* skip */
          }
        }),
      );
    }
    versions.sort((a, b) => (b.version || 0) - (a.version || 0));
    return { current, versions };
  } catch {
    return { current: null, versions: [] };
  }
}

export async function loadLotteryHistory() {
  try {
    const current = await fetch("/data/lottery-current.json", { cache: "no-store" }).then((r) =>
      r.ok ? r.json() : null,
    );
    const versions = [];
    const max = current?.version || 0;
    if (max > 0) {
      await Promise.all(
        Array.from({ length: max }, (_, i) => i + 1).map(async (v) => {
          try {
            const r = await fetch(`/data/lottery-history/v${v}.json`, { cache: "no-store" });
            if (r.ok) versions.push(await r.json());
          } catch {
            /* skip */
          }
        }),
      );
    }
    versions.sort((a, b) => (b.version || 0) - (a.version || 0));
    return { current, versions };
  } catch {
    return { current: null, versions: [] };
  }
}

export function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1)}%`;
}

export function shortMint(m, n = 4) {
  if (!m) return "—";
  return `${m.slice(0, n)}…${m.slice(-n)}`;
}

export function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function fmtSol(n, digits = 4) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v === 0) return "0";
  if (v < 0.0001) return v.toExponential(2);
  return v.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export function claimableSol(fees) {
  const q = fees?.claimable?.quote;
  if (!q) return null;
  if (q.amountTokens != null) return Number(q.amountTokens);
  if (q.amountRaw != null && q.decimals != null) {
    return Number(q.amountRaw) / 10 ** Number(q.decimals);
  }
  return null;
}

/** Human-readable spend / gate skip codes from the worker. */
export function explainSpendSkip(code) {
  switch (code) {
    case "below_min_distribute":
    case "gate_below_min":
      return "Fees too low this window — liquid cycle budget was under the minimum to buy the board.";
    case "token_budget_below_min":
      return "After ATA rent reserve, not enough SOL left to buy basket tokens.";
    case "dry_run":
      return "Dry-run only — no on-chain spends.";
    default:
      return code ? `Skipped (${code}).` : null;
  }
}

/**
 * Whether the latest spend attempt is waiting on more fees.
 * Prefers `spend.lastAttempt` (gate check) over archived cycle skip.
 */
export function spendWaitingOnFees(spend) {
  if (!spend) return false;
  const attempt = spend.lastAttempt;
  if (attempt?.skipped === "gate_below_min" || attempt?.skipped === "below_min_distribute") {
    return true;
  }
  if (!spend.skipped) return false;
  const paid =
    (spend.swaps && spend.swaps.length > 0) ||
    (spend.distributes && spend.distributes.length > 0);
  return !paid && (
    spend.skipped === "below_min_distribute" ||
    spend.skipped === "token_budget_below_min"
  );
}

export function spendStatusCopy(spend, schedule = {}) {
  const minSol = schedule.minDistributeSol ?? 0.02;
  const attempt = spend?.lastAttempt;
  const code = attempt?.skipped || spend?.skipped || null;
  const waiting = spendWaitingOnFees(spend);
  if (!waiting) {
    return {
      waiting: false,
      code: null,
      title: null,
      detail: null,
    };
  }
  const cycleSol = attempt?.cycleSol ?? spend?.cycleSol;
  const detailParts = [
    explainSpendSkip(code),
    `Payouts need ≥ ${minSol} SOL liquid (after reserve) per window.`,
  ];
  if (cycleSol != null && Number.isFinite(Number(cycleSol))) {
    detailParts.push(`Last attempt budget: ${fmtSol(cycleSol)} SOL.`);
  }
  detailParts.push("Fees keep accruing from $SI volume — next clock tick will retry.");
  return {
    waiting: true,
    code,
    title: "Waiting on fees",
    detail: detailParts.filter(Boolean).join(" "),
  };
}
